import { createFileRoute, Outlet } from "@tanstack/react-router";
import { trpc, trpcUtils } from "@/hooks/trpc";
import { HarvestOperationTemplateId } from "@brewbase/db/types";
import { z } from "zod";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import { Layers } from "lucide-react";
import { useMemo, useState } from "react";
import { OperationTemplateSidebarHeader } from "@/features/harvest-operation-template/sidebar-header";
import { RevisionHistoryDrawer } from "@/features/harvest-operation-template/revision-history-drawer";
import { HarvestFormulaEvalScopeProvider } from "@/features/harvest-operation-template/HarvestFormulaEvalScopeProvider";
import { RequireCapability } from "@/components/require-capability";

export const Route = createFileRoute(
  "/harvests/operation-templates/$template_id",
)({
  component: () => (
    <RequireCapability cap="harvest-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
  params: z.object({
    template_id: z.coerce
      .number()
      .transform((val) => val as HarvestOperationTemplateId),
  }),
  loader: async ({ params }) => {
    await Promise.all([
      trpcUtils.harvest.operationTemplates.revisionGetDraftForTemplate.ensureData(
        params.template_id,
      ),
      trpcUtils.harvest.operationTemplates.templatesList.ensureData({
        includeArchived: true,
      }),
    ]);
  },
});

function RouteComponent() {
  const { template_id } = Route.useParams();
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: draftRevision, isLoading } =
    trpc.harvest.operationTemplates.revisionGetDraftForTemplate.useQuery(
      template_id,
    );
  const { data: templates = [] } =
    trpc.harvest.operationTemplates.templatesList.useQuery({
      includeArchived: true,
    });
  const { data: attributes = [] } =
    trpc.harvest.operationTemplates.attributeList.useQuery(
      draftRevision?.id ?? (0 as never),
      { enabled: !!draftRevision },
    );
  const { data: kpis = [] } = trpc.harvest.templateKpis.listForTemplate.useQuery(
    draftRevision?.id ?? (0 as never),
    { enabled: !!draftRevision },
  );

  // Find the template this url points at.
  const template = useMemo(
    () => templates.find((t) => t.id === template_id),
    [templates, template_id],
  );

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "Operation Templates",
      icon: Layers,
      href: "/harvests/operation-templates",
    },
    customHeader: template ? (
      <OperationTemplateSidebarHeader
        template={template}
        onOpenHistory={() => setHistoryOpen(true)}
      />
    ) : null,
    navigation: [
      {
        type: "link",
        id: "general",
        label: "General",
        href: `/harvests/operation-templates/${template_id}`,
        exact: true,
      },
      {
        type: "link",
        id: "attributes",
        label: "Attributes",
        href: `/harvests/operation-templates/${template_id}/attributes`,
      },
      {
        type: "link",
        id: "samples",
        label: "Samples",
        href: `/harvests/operation-templates/${template_id}/samples`,
      },
      {
        type: "link",
        id: "kpis",
        label: "KPIs",
        href: `/harvests/operation-templates/${template_id}/kpis`,
      },
    ],
  });

  if (isLoading || !draftRevision) {
    return (
      <div className="flex items-center justify-center h-full">
        Loading template...
      </div>
    );
  }

  return (
    <HarvestFormulaEvalScopeProvider templateId={draftRevision.id}>
      <div className="h-full">
        <Outlet />
        {template && (
          <RevisionHistoryDrawer
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            templateId={template.id}
            draftRevision={draftRevision}
          />
        )}
      </div>
    </HarvestFormulaEvalScopeProvider>
  );
}
